import { useMemo, useState } from 'react';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { GroupDescriptionType } from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './useInputDataVersions.translations.json';
import { InputDataVersionType } from './types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-versions-hook',
);
addTranslations(translations);

export function useInputDataVersions(
  descriptions?: GroupDescriptionType[],
  isOutput?: boolean,
) {
  const { t } = useTranslation();
  const [version, setVersion] = useState<InputDataVersionType | undefined>(
    isOutput ? 'output' : undefined,
  );

  const versionOptions = useMemo(() => {
    let hasActiveVersion = false;
    let hasDraftVersion = false;
    descriptions?.forEach((description) => {
      if (description.versions?.some((version) => version.draftVersion)) {
        hasDraftVersion = true;
      }
      if (description.versions?.some((version) => version.activeVersion)) {
        hasActiveVersion = true;
      }
    });
    const versionOptions: DefaultOptionType[] = [];
    if (hasActiveVersion) {
      versionOptions.push({
        label: <span className="inUse">{t('status.inUse')}</span>,
        value: 'active',
        className: 'inUse',
      });
    }
    if (hasDraftVersion) {
      versionOptions.push({
        label: <span className="pending">{t('status.pending')}</span>,
        value: 'draft',
        className: 'pending',
      });
    }
    if (versionOptions.length) {
      versionOptions.push({
        label: <span className="outputBased">{t('status.outputBased')}</span>,
        value: 'output',
        className: 'outputBased',
      });
    }

    setVersion((prev) => {
      const hasValue =
        versionOptions?.some((option) => option.value === prev) ?? false;
      if (versionOptions?.length) {
        if (!prev || !hasValue) {
          return versionOptions[0].value as InputDataVersionType;
        }
      }

      if (!hasValue && prev !== 'output') {
        return undefined;
      }
      return prev;
    });

    return versionOptions;
  }, [descriptions, t]);

  return { version, versionOptions, setVersion };
}
